/**
 * Category Utilities
 * Shared functions for building dynamic category lookups from API data.
 * Replaces hardcoded ACTIVITY_TYPES constants.
 */

import {
  TruckIcon,
  AcademicCapIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentIcon,
  FireIcon,
  HeartIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  BoltIcon,
  StarIcon,
  FlagIcon,
  MegaphoneIcon,
  BeakerIcon,
  CogIcon,
  HandRaisedIcon,
  GlobeAltIcon,
  CommandLineIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  CubeIcon,
  SparklesIcon,
  PuzzlePieceIcon,
  RocketLaunchIcon,
  LightBulbIcon,
  MusicalNoteIcon,
  PaintBrushIcon,
  ScaleIcon,
  MapPinIcon,
  PhoneIcon,
  WrenchIcon,
  HomeIcon,
  BookOpenIcon,
  ChatBubbleLeftIcon,
  CameraIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline'

/**
 * Maps icon name strings (from DB) to Heroicon components
 */
const ICON_MAP = {
  TruckIcon,
  AcademicCapIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentIcon,
  FireIcon,
  HeartIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  BoltIcon,
  StarIcon,
  FlagIcon,
  MegaphoneIcon,
  BeakerIcon,
  CogIcon,
  HandRaisedIcon,
  GlobeAltIcon,
  CommandLineIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  CubeIcon,
  SparklesIcon,
  PuzzlePieceIcon,
  RocketLaunchIcon,
  LightBulbIcon,
  MusicalNoteIcon,
  PaintBrushIcon,
  ScaleIcon,
  MapPinIcon,
  PhoneIcon,
  WrenchIcon,
  HomeIcon,
  BookOpenIcon,
  ChatBubbleLeftIcon,
  CameraIcon,
  VideoCameraIcon
}

export const getIconComponent = (iconName) => {
  return ICON_MAP[iconName] || ClipboardDocumentIcon
}

/**
 * Derives bgColor and borderColor from the color field.
 * e.g., "text-red-500" → { bgColor: "bg-red-500/10", borderColor: "border-red-500" }
 */
export const deriveCategoryColors = (color) => {
  if (!color) {
    return { bgColor: 'bg-slate-500/10', borderColor: 'border-slate-500' }
  }

  // Extract the color part: "text-red-500" → "red-500"
  const match = color.match(/text-(.+)$/)
  if (!match) {
    return { bgColor: 'bg-slate-500/10', borderColor: 'border-slate-500' }
  }

  const colorPart = match[1]
  return {
    bgColor: `bg-${colorPart}/10`,
    borderColor: `border-${colorPart}`
  }
}

/**
 * Builds a { [slug]: { label, shortLabel, Icon, color, bgColor, borderColor } } lookup
 * that is a drop-in replacement for the hardcoded ACTIVITY_TYPES constant.
 */
export const buildCategoryLookup = (categories) => {
  const lookup = {}

  for (const cat of categories) {
    const { bgColor, borderColor } = deriveCategoryColors(cat.color)
    lookup[cat.slug] = {
      label: cat.name,
      shortLabel: cat.name,
      Icon: getIconComponent(cat.icon),
      color: cat.color || 'text-slate-400',
      bgColor,
      borderColor
    }
  }

  // Guarantee an 'other' fallback so `lookup[key] || lookup.other` never
  // resolves to undefined — even if the department has no 'other' category.
  if (!lookup.other) {
    lookup.other = FALLBACK_ACTIVITY_TYPES.other
  }

  return lookup
}

/**
 * Static fallback for the loading state (before categories are fetched).
 * Uses only the default system categories with their standard icons/colors.
 */
export const FALLBACK_ACTIVITY_TYPES = {
  incident: {
    label: 'Incident Response',
    shortLabel: 'Incident',
    Icon: TruckIcon,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500'
  },
  training: {
    label: 'Training',
    shortLabel: 'Training',
    Icon: AcademicCapIcon,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500'
  },
  meeting: {
    label: 'Meeting',
    shortLabel: 'Meeting',
    Icon: UserGroupIcon,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500'
  },
  worknight: {
    label: 'Work Night',
    shortLabel: 'Work Night',
    Icon: WrenchScrewdriverIcon,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500'
  },
  other: {
    label: 'Other',
    shortLabel: 'Other',
    Icon: ClipboardDocumentIcon,
    color: 'text-slate-400',
    bgColor: 'bg-slate-500/10',
    borderColor: 'border-slate-500'
  }
}
